import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import {
  useContactBasicFilters,
  IUseContactBasicFiltersReturn,
} from 'src/pages/Contacts/CustomHooks/useContactBasicFilters';
import {
  useContactAdvancedFilters,
  IUseContactAdvancedFiltersReturn,
  IContactAdvancedFilters,
} from 'src/pages/Contacts/CustomHooks/useContactAdvancedFilters';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { USER_ROLES } from 'src/common/constants';

export interface IUseContactsPageOrchestratorReturn {
  contacts: IContact[];
  isLoadingContacts: boolean;

  refetchContacts: () => Promise<void>;
  deleteContactById: (id: string) => Promise<void>;

  basicSearchTerm: string;
  updateBasicSearchTerm: (value: string) => void;
  clearBasicSearchTerm: () => void;
  isBasicSearchEngaged: boolean;

  activeFilters: IContactAdvancedFilters;
  handleApplyFilters: (filters: IContactAdvancedFilters) => void;
  resetFilters: () => void;
  removeSingleActiveFilter: (filterText: any) => void;
  hasActiveFilters: boolean;
  uniqueContactStatuses: string[];

  canEditContactEmail: boolean;
}

export const useContactsPageOrchestrator =
  (): IUseContactsPageOrchestratorReturn => {
    const user: any = useSelector(selectAuthenticatedUser);

    const userRole: string | undefined = user?.roles?.find(
      (wrapper: any) => wrapper?.role?.name
    )?.role?.name;

    const contactAPI: UseContactAPIReturn = useContactAPI();

    const basic: IUseContactBasicFiltersReturn = useContactBasicFilters(
      contactAPI.contacts
    );

    const advanced: IUseContactAdvancedFiltersReturn = useContactAdvancedFilters(
      contactAPI.contacts
    );

    useEffect(() => {
      if (!user?.id || !userRole) {
        return;
      }

      void contactAPI.fetchContacts();
    }, [user?.id, userRole]);

    const contacts: IContact[] = basic.isEngaged
      ? basic.contacts
      : advanced.contacts;

    const refetchContacts = async (): Promise<void> => {
      await contactAPI.fetchContacts();
    };

    const deleteContactById = async (id: string): Promise<void> => {
      await contactAPI.deleteContact(id);
    };

    const canEditContactEmail: boolean =
      userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;

    return {
      contacts,
      isLoadingContacts: contactAPI.isLoadingContacts,

      refetchContacts,
      deleteContactById,

      basicSearchTerm: basic.basicSearchTerm,
      updateBasicSearchTerm: basic.updateBasicSearchTerm,
      clearBasicSearchTerm: basic.clearBasicSearchTerm,
      isBasicSearchEngaged: basic.isEngaged,

      activeFilters: advanced.activeFilters,
      handleApplyFilters: advanced.handleApplyFilters,
      resetFilters: advanced.resetFilters,
      removeSingleActiveFilter: advanced.removeSingleActiveFilter,
      hasActiveFilters: advanced.hasActiveFilters,
      uniqueContactStatuses: advanced.uniqueContactStatuses,

      canEditContactEmail,
    };
  };
